import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import CloseIcon from "../../assets/svg/CloseIcon";

export default function PdfModal({ document: doc, onClose }) {
  const closeButtonRef = useRef(null);
  const lastFocusedRef = useRef(null);

  useEffect(() => {
    if (!doc) return undefined;
    lastFocusedRef.current = document.activeElement;
    const handleKeyDown = (event) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", handleKeyDown);
    closeButtonRef.current && closeButtonRef.current.focus();
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      lastFocusedRef.current && lastFocusedRef.current.focus && lastFocusedRef.current.focus();
    };
  }, [doc, onClose]);

  if (!doc) return null;
  return (
    <ModalWrapper role="dialog" aria-modal="true" aria-labelledby="pdf-modal-title">
      <Backdrop onClick={onClose} />
      <ModalContent>
        <ModalHeader>
          <div><small>Company document</small><h2 id="pdf-modal-title">{doc.title}</h2></div>
          <CloseBtn onClick={onClose} aria-label="Close document viewer" ref={closeButtonRef}><CloseIcon /></CloseBtn>
        </ModalHeader>
        <ModalBody><iframe src={`/${doc.fileName}`} title={doc.title} width="100%" height="100%" /></ModalBody>
        <ModalFooter>
          <p>View the document above or save a copy for your records.</p>
          <DownloadButton href={`/${doc.fileName}`} download={doc.downloadName}>Download PDF <span aria-hidden="true">↓</span></DownloadButton>
        </ModalFooter>
      </ModalContent>
    </ModalWrapper>
  );
}

const ModalWrapper = styled.div`position: fixed; inset: 0; z-index: 10000; display: flex; justify-content: center; align-items: center; padding: 24px;`;
const Backdrop = styled.div`position: absolute; inset: 0; background: rgba(10,9,25,.76); backdrop-filter: blur(8px);`;
const ModalContent = styled.div`position: relative; z-index: 2; width: min(900px, 100%); height: min(90vh, 920px); display: flex; flex-direction: column; overflow: hidden; border-radius: 24px; background: #fff; box-shadow: 0 30px 90px rgba(0,0,0,.35);`;
const ModalHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: 20px; padding: 18px 22px; border-bottom: 1px solid #e3e7e5;
  small { color: #0a9250; font-size: .62rem; font-weight: 800; letter-spacing: .13em; text-transform: uppercase; }
  h2 { margin-top: 2px; color: #17152f; font-size: 1rem; }
`;
const CloseBtn = styled.button`width: 42px; height: 42px; display: grid; place-items: center; flex: 0 0 auto; border: 0; border-radius: 50%; background: #17152f; cursor: pointer; transform: scale(.88); &:hover { background: #0a9250; }`;
const ModalBody = styled.div`flex: 1; min-height: 0; background: #eef1ef; iframe { display: block; border: 0; }`;
const ModalFooter = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: 20px; padding: 15px 22px;
  p { color: #737886; font-size: .72rem; }
  @media (max-width: 600px) { p { display: none; } }
`;
const DownloadButton = styled.a`display: inline-flex; align-items: center; gap: 10px; padding: 11px 18px; border-radius: 999px; background: #0a9250; color: #fff; font-size: .76rem; font-weight: 800; &:hover { background: #077d44; color: #fff; }`;
